import type { FC } from 'react';
import React, { useState, useEffect, useReducer } from 'react';
import { EmployeeCard } from './fa-employee-card';
import type { EmployeeTypes, IEmployeeDataReducerAction } from 'types';
import { DepartmentDataReducerActionTypes } from 'types';
import Dropdown from '../dropdown-as-select/dropdown-as-select';
import generateQueryParams from '../../foundation/methods/generateQueryParams';
import deepClone from '../../foundation/methods/deepClone/deepClone';
import { LoadingSpinner } from '../fa-spinner/fa-spinner';
import mapRawData from './fa-employee-data-mapping';

const employeeReducer = (
    employeeState: EmployeeTypes.IEmployeesMapped,
    action: IEmployeeDataReducerAction
): EmployeeTypes.IEmployeesMapped => {
    const { type, payload } = action;

    switch (type) {
        case DepartmentDataReducerActionTypes.SET_NEW_EMPLOYEE_DATA:
            return {
                ...employeeState,
                ...payload,
            };

        case DepartmentDataReducerActionTypes.SET_NEW_ORIGINAL_EMPLOYEE_DATA:
            return {
                ...employeeState,
                originalEmployeeFacets: payload,
            };

        default:
            throw new Error();
    }
};

export const Employees: FC<EmployeeTypes.IEmployeeProps> = (props: EmployeeTypes.IEmployeeProps) => {
    const { pageContentGuid, blockContentGuid, heading } = props;
    const [employeeData, dispatchEmployeeData] = useReducer(employeeReducer, undefined!);
    const [loadingState, setLoadingState] = useState(false);
    const [queryParams, setQueryParams] = useState<EmployeeTypes.IQueryParams>({ pageContentGuid, blockContentGuid });
    const [firstComponentDataFetchCompleted, setFirstComponentDataFetchCompleted] = useState(false);

    useEffect(() => {
        /**
         * Only called on render, in order to set the "original employee facets"
         */
        fetchEmployeeData().then((employeeData) => {
            dispatchEmployeeData({
                type: DepartmentDataReducerActionTypes.SET_NEW_EMPLOYEE_DATA,
                payload: employeeData,
            });
            dispatchEmployeeData({
                type: DepartmentDataReducerActionTypes.SET_NEW_ORIGINAL_EMPLOYEE_DATA,
                payload: employeeData.facets,
            });
        });
        setFirstComponentDataFetchCompleted(true);
    }, []);

    useEffect(() => {
        fetchEmployeeData().then((employeeData) => {
            dispatchEmployeeData({
                type: DepartmentDataReducerActionTypes.SET_NEW_EMPLOYEE_DATA,
                payload: employeeData,
            });

            if (firstComponentDataFetchCompleted) {
                fuseFacetData(employeeData);
            }
        });
    }, [queryParams]);

    const fuseFacetData = (newEmployeeData: EmployeeTypes.IEmployeesMapped) => {
        /**
         * We combine standard facets with available facets based on chosen dropdown input,
         * in order to display facets not available (greyed out)
         */

        const originalEmployeeData = employeeData;
        const fusedFacetData = originalEmployeeData.originalEmployeeFacets?.map(
            (originalFacet: EmployeeTypes.IFacet, originalFacetIndex: number) => {
                const currentNewFacet = newEmployeeData.facets[originalFacetIndex];
                const modifiedFacets = originalFacet?.values.map((originalFacetValue: EmployeeTypes.IValue) => {
                    if (
                        currentNewFacet.values.find((i: EmployeeTypes.IValue) => i.name == originalFacetValue.name) ===
                        undefined
                    ) {
                        originalFacetValue.available = false;
                    } else {
                        originalFacetValue.available = true;
                    }

                    return originalFacetValue;
                });

                originalFacet.values = modifiedFacets;

                return {
                    ...originalFacet,
                    values: originalFacet.values,
                };
            }
        );

        dispatchEmployeeData({
            type: DepartmentDataReducerActionTypes.SET_NEW_ORIGINAL_EMPLOYEE_DATA,
            payload: fusedFacetData,
        });
    };

    const fetchEmployeeData = async () => {
        setLoadingState(true);

        let employeeData;
        let url = '/[language]/api/staff/search?';

        const language = document.documentElement.lang;
        url = `${url?.replace('[language]', language)}${generateQueryParams(queryParams)}`;

        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error(response.statusText);
            setLoadingState(false);
            employeeData = await response.json();
        } catch (e) {
            console.log(e);
        }

        return mapRawData(employeeData);
    };

    const setQueryParamsFromDropdownSelect = async (
        _selectedItemValue: string,
        dropdownType: string,
        dropdownKey: string
    ) => {
        const newQueryParams = deepClone(queryParams);

        employeeData.originalEmployeeFacets?.forEach((facet) => {
            if (dropdownType === facet.key) {
                newQueryParams[`${facet.key.toLocaleLowerCase()}` as keyof typeof newQueryParams] = dropdownKey;
                setQueryParams((prevState) => ({ ...prevState, ...newQueryParams }));
            }
        });
    };

    const renderEmployeeCards = () => employeeData?.staffContacts?.map((employee, index) => <EmployeeCard key={index} {...employee} />);

    const renderDropdownFilter = () => employeeData?.originalEmployeeFacets?.map((facet, index) => {
            const currentSelectedFilters = employeeData?.usedFilters;
            const currentDropdownValue = currentSelectedFilters[facet.key as keyof typeof currentSelectedFilters];
            const currentDropdownValueAsName = facet.values.find((name) => name.key === currentDropdownValue)?.name;

            return (
                <Dropdown
                    currentSelectedValue={currentDropdownValueAsName}
                    key={index}
                    dropdownType={facet.key}
                    items={facet.values}
                    defaultSelectedPropName={facet.name}
                    callback={setQueryParamsFromDropdownSelect}
                />
            );
        });

    return (
        <section className="fa-employees">
            <h2 className="fa-employees__title fa-theme-color">{heading}</h2>

            {loadingState ? (
                <article className="fa-spinner__container">
                    <LoadingSpinner />
                </article>
            ) : (
                <React.Fragment>
                    <div className="fa-employees__filter">{renderDropdownFilter()}</div>

                    <div className="fa-employees__cards">{renderEmployeeCards()}</div>
                </React.Fragment>
            )}
        </section>
    );
};
